import React from 'react';
import { Card, CardContent, Typography, Box } from '@mui/material';

export interface StatCardProps {
  title: string;
  subtitle?: string;
  value: number | string;
  icon: React.ReactNode;
  iconShape?: 'square' | 'circle';
  // 比較データ（Analytics画面で使用）
  comparisonValue?: number | string;
  // カスタムスタイル
  variant?: 'dashboard' | 'analytics';
  sx?: Record<string, any>;
}

export const StatCard: React.FC<StatCardProps> = ({
  title,
  subtitle,
  value,
  icon,
  iconShape = 'circle',
  comparisonValue,
  variant = 'dashboard',
  sx,
}) => {
  const isDashboard = variant === 'dashboard';
  const isAnalytics = variant === 'analytics';

  // 比較データの差分を計算
  const getComparisonDiff = (): { diff: number | null; isPercentage: boolean } | null => {
    if (comparisonValue === undefined || comparisonValue === null) return null;
    
    const current = typeof value === 'string' ? parseFloat(value.replace(/[^0-9.-]/g, '')) : value;
    const comparison = typeof comparisonValue === 'string' 
      ? parseFloat(comparisonValue.toString().replace(/[^0-9.-]/g, '')) 
      : comparisonValue;
    
    if (isNaN(current) || isNaN(comparison)) return null;

    const isPercentage = typeof value === 'string' && value.includes('%');
    const diff = current - comparison;

    return { diff, isPercentage };
  };

  const comparisonInfo = getComparisonDiff();

  // 値をフォーマット
  const formatValue = (val: number | string): string => {
    if (typeof val === 'number') {
      return val.toLocaleString();
    }
    return val.toString();
  };

  // アイコンのサイズ
  const iconSize = isAnalytics ? 48 : 50;
  const iconFontSize = isAnalytics ? 24 : undefined;

  return (
    <Card
      sx={{
        height: '100%',
        background: '#FFFFFF 0% 0% no-repeat padding-box',
        boxShadow: isDashboard 
          ? '0px 3px 12px #00000026' 
          : '0px 2px 8px rgba(0, 0, 0, 0.1)',
        borderRadius: isDashboard ? '24px' : '12px',
        ...(isAnalytics && {
          flex: '1 1 calc(25% - 16px)',
          minWidth: 200,
          maxWidth: 'calc(25% - 16px)',
        }),
        ...sx,
      }}
    >
      <CardContent sx={{ p: isDashboard ? 2 : 1.5 }}>
        {/* タイトル */}
        <Typography
          color={isDashboard ? 'textSecondary' : undefined}
          variant={isDashboard ? 'body2' : undefined}
          sx={{
            ...(isDashboard
              ? {
                  mb: 1.5,
                  fontSize: '16px',
                  color: '#000000',
                }
              : {
                  fontSize: '16px',
                  color: '#000000',
                  fontWeight: 600,
                  mb: 1,
                }),
          }}
        >
          {title}
          {subtitle && isAnalytics && (
            <span style={{ fontSize: '12px', color: '#666666', fontWeight: 400 }}>
              {' '}{subtitle}
            </span>
          )}
        </Typography>

        {/* コンテンツ */}
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          sx={{
            mt: isDashboard ? 1 : 0,
            gap: isAnalytics ? 2 : 0,
          }}
        >
          {/* アイコン */}
          <Box
            sx={{
              background: 'linear-gradient(135deg, rgba(255, 152, 0, 0.3) 0%, rgba(255, 235, 59, 0.2) 30%, rgba(33, 150, 243, 0.8) 100%)',
              borderRadius: iconShape === 'circle' ? '50%' : '12px',
              width: iconSize,
              height: iconSize,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#1B76D2',
              flexShrink: 0,
              '& svg': {
                fontSize: iconFontSize || 'inherit',
                color: '#1B76D2',
              },
            }}
          >
            {icon}
          </Box>

          {/* 値 */}
          <Box
            display="flex"
            flexDirection={isAnalytics ? 'column' : 'row'}
            alignItems={isAnalytics ? 'flex-end' : 'center'}
            sx={{
              ...(isAnalytics && { flex: 1 }),
              ...(isDashboard && { ml: 2 }),
            }}
          >
            <Typography
              variant={isAnalytics ? 'h4' : 'h3'}
              component="div"
              sx={{
                fontWeight: isAnalytics ? 600 : 700,
                color: '#000000',
                fontSize: isAnalytics ? undefined : '32px',
                lineHeight: isAnalytics ? 1.2 : undefined,
                mb: isAnalytics ? -0.5 : 0,
              }}
            >
              {formatValue(value)}
            </Typography>

            {/* 比較データ（Analyticsのみ） */}
            {isAnalytics && comparisonInfo && comparisonInfo.diff !== null && (
              <Typography sx={{ fontSize: '12px', lineHeight: 1.2 }}>
                <span style={{ color: '#9E9E9E' }}>(</span>
                {comparisonInfo.diff === 0 ? (
                  <span style={{ color: '#9E9E9E' }}>
                    ±0{comparisonInfo.isPercentage ? '%' : ''}
                  </span>
                ) : comparisonInfo.diff > 0 ? (
                  <span style={{ color: '#FF9800' }}>
                    ↑+{comparisonInfo.isPercentage 
                      ? `${Math.round(Math.abs(comparisonInfo.diff))}%`
                      : Math.abs(comparisonInfo.diff).toLocaleString()}
                  </span>
                ) : (
                  <span style={{ color: '#1B76D2' }}>
                    ↓{comparisonInfo.isPercentage 
                      ? `${Math.round(comparisonInfo.diff)}%`
                      : comparisonInfo.diff.toLocaleString()}
                  </span>
                )}
                <span style={{ color: '#9E9E9E' }}>)</span>
              </Typography>
            )}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

